import { Component, effect, inject } from '@angular/core';
import { InformationBlock } from '../information-block/information-block';
import { CreateContentService } from '../../services/create-content.service';
import { PublicationBlock } from '../publication-block/publication-block';
import { VisibilityType } from '../../types/visibility.type';
import { FormGroup, FormControl, Validators, FormControlStatus } from '@angular/forms';
import { PlanningType } from '../../types/planning.type';
import { Observable, Subscription } from 'rxjs';
import { WorkoutSportType } from '../../types/sport.type';
import { TrainingBlock } from '../training-block/training-block';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { SectionModelType } from '../../types/section.type';
import { MediaBlock } from '../media-block/media-block';

@Component({
  selector: 'app-creation-workout-form',
  imports: [MediaBlock, TrainingBlock, InformationBlock, PublicationBlock],
  templateUrl: './creation-workout-form.html',
  styleUrl: './creation-workout-form.scss',
})
export class CreationWorkoutForm {
  createContentService: CreateContentService = inject(CreateContentService);

  workoutForm: FormGroup = new FormGroup({
    trainingBlock: new FormGroup({
      sport: new FormControl<WorkoutSportType | undefined>(undefined, Validators.required),
      workoutMode: new FormControl<WorkoutMode | undefined>(undefined, Validators.required),
      sections: new FormControl<SectionModelType[]>([], Validators.required),
    }),
    informationBlock: new FormGroup({
      title: new FormControl<string | undefined>(undefined, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(128),
      ]),
      theme: new FormControl<string | undefined>(undefined),
      description: new FormControl<string | undefined>(undefined, [Validators.maxLength(256)]),
    }),
    publicationBlock: new FormGroup({
      visibility: new FormControl<null | VisibilityType>('public', Validators.required),
      planning: new FormControl<null | PlanningType>('immediate', Validators.required),
      planningDate: new FormControl<Date | null>(null),
      planningTime: new FormControl<string | null>(null),
    }),
  });
  subscriptions: Subscription[] = [];

  constructor() {
    effect(() => {
      this.workoutForm
        .get('trainingBlock.sections')!
        .setValue(this.createContentService.workoutSections());
    });
  }

  get TrainingBlockFormGroup(): FormGroup {
    return this.workoutForm.get('trainingBlock') as FormGroup;
  }

  get publicationBlockFormGroup(): FormGroup {
    return this.workoutForm.get('publicationBlock') as FormGroup;
  }

  get informationBlockFormGroup(): FormGroup {
    return this.workoutForm.get('informationBlock') as FormGroup;
  }

  markAllErrors(): void {
    this.workoutForm.markAllAsTouched();
  }

  canDeactivate(): Observable<boolean> {
    return this.createContentService.canDeactivate(this.workoutForm, 'workout');
  }

  ngOnInit(): void {
    const isEditing: boolean = this.createContentService.editedContentId() !== undefined;
    const localSave: string | null = isEditing ? null : localStorage.getItem('workout_form');
    if (localSave) this.workoutForm.patchValue(JSON.parse(localSave));
    this.createContentService.restoreForm(this.workoutForm, 'workout');
    if (isEditing) this.workoutForm.markAsPristine();

    this.workoutForm.statusChanges.subscribe((status: FormControlStatus) => {
      if (status === 'VALID') {
        this.createContentService.workoutFormIsValid.set(true);
      } else {
        this.createContentService.workoutFormIsValid.set(false);
      }
    });

    this.subscriptions.push(
      this.workoutForm.get('trainingBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.workoutSport.set(values.sport ?? undefined);
        this.createContentService.setSharedSport(values.sport ?? undefined);
      }),
      this.workoutForm.get('informationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.title.set(values.title ?? undefined);
        this.createContentService.description.set(values.description ?? '');
        this.createContentService.workoutThemes.set(values.theme ? [values.theme] : []);
      }),
      this.workoutForm.get('publicationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.updatePublication(values);
      }),
    );

    const pubBlock = this.workoutForm.get('publicationBlock')!;
    this.createContentService.updatePublication(pubBlock.value);

    const trainingBlock = this.workoutForm.get('trainingBlock')!;
    this.createContentService.workoutSport.set(trainingBlock.get('sport')!.value ?? undefined);

    const infoBlock = this.workoutForm.get('informationBlock')!;
    this.createContentService.title.set(infoBlock.get('title')!.value ?? undefined);
    this.createContentService.description.set(infoBlock.get('description')!.value ?? '');
    const theme: string | undefined = infoBlock.get('theme')!.value ?? undefined;
    this.createContentService.workoutThemes.set(theme ? [theme] : []);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }
}
